import { fabric } from 'fabric'
import axios from 'axios'
import { padStart } from 'lodash-es'
import type Merch from '../merch'
import { findParentPath } from '../../utils'
import type { IMerchTemplate, IMerchTemplateFileMapping, IMerchTemplateOption } from './IMerchTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { appConstants, merchConstants } from '@/models/constants'
import type MyArticle from '@/models/myArticle'
import utils from '@/services/utils'
import footwearTechSheetImage from '@/assets/templateLayoutImages/footwearTechSheet.png'

class FootwearTechSheet implements IMerchTemplate {
  id = merchConstants.slideGenTemplatesId.footwearTechSheet
  name = 'Footwear Tech Sheet'
  layoutImage = footwearTechSheetImage
  imgSize: number = 500
  slideWidth: number = 1280
  slideHeight: number = 720
  imgHorSpacing: number = 40
  imgVerSpacing: number = 20
  logoWidth = 155
  logoHeight = 87
  logoScaling = 0.20
  uaNameLogoWidth = 155
  uaNameLogoHeight = 15
  uaNameLogoScaling = 1
  leftMarginForModel = 30
  startPositionX = 0
  startPositionY = 0
  imageHeight: number = 500
  imageWidth: number = 500
  font: string = 'Neue Plak'
  rightMargin = 20
  imageDefaultScaleFactor: number = 0.90
  leftPadding: number = 50
  rightPadding: number = 30
  topPadding: number = 50
  bottomPadding: number = 50
  uaHeaderIconLogoData: string = ''
  uaHeaderNameLogoData: string = ''
  articleImageProps = {
    topPadding: 2,
  }

  modelNameProps = {
    width: 325,
    fontSize: 54,
    fontWeight: 'bold',
    topPadding: 50,
    leftPadding: 120,
    bottomPadding: 30,
    textAlign: 'left',
    lock: false,
    fill: '#ffffff',
  }

  modelDetailsProps = {
    padding: 17,
    fontSize: 14,
    width: 325,
    fontWeight: 'bold',
    textAlign: 'left',
    lock: false,
    fill: '#ffffff',
  }

  headerImageNameLogoProps = {
    bottomPadding: 180,
  }

  textBoxProps = {
    width: 325,
    topPadding: 20,
    bottomPadding: 10,
    leftPadding: 120,
    fontSize: 14,
    fontWeight: 'normal',
    textAlign: 'left',
    fill: '#ffffff',
    fontStyle: 'normal',
  }

  needToKnowstextBoxProps = {
    width: 310,
    topPadding: 10,
    bottomPadding: 150,
    leftPadding: 80,
    fontSize: 34,
    fontWeight: 'bold',
    textAlign: 'left',
    fill: '#ffffff',
    fontStyle: 'normal',
  }

  ponitsTextBoxProps = {
    topPadding: 40,
    leftPadding: 350,
  }

  circleObjProps = {
    bulletCount: 5,
    circleSpacing: 25,
    width: 14,
    height: 14,
    topPadding: 20,
    rightPadding: 100,
    fill: '#FF0101',
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'left',
    fontStyle: 'normal',
  }

  getOptions(): IMerchTemplateOption[] {
    const options: IMerchTemplateOption[] = []
    options.push({
      name: 'imageType',
      label: 'generateFrameDialog.steps.options.imageType',
      type: 'list',
      default: 'newestImage',
      data: merchConstants.defaultImageType,
      required: false,
      multiple: false,
      valueProp: 'key',
      displayProp: 'value',
    })

    // const availableFrameSizes: { key: FrameSize, label: string }[] = [
    //   { key: 'wide', label: 'Wide' },
    //   { key: 'a4', label: 'A4' },
    //   { key: 'letter', label: 'Letter' },
    // ]

    // options.push({
    //   name: 'frameSize',
    //   label: 'generateFrameDialog.steps.options.frameSize',
    //   type: 'list',
    //   default: 'wide',
    //   required: true,
    //   multiple: false,
    //   data: availableFrameSizes,
    //   valueProp: 'key',
    //   displayProp: 'label',
    // })

    return options
  }

  getFileMapping(): IMerchTemplateFileMapping[] {
    return [{
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code'],
    }]
  }

  async generate(catalog: CatalogDetails, articlesData: MyArticle[], options: Record<string, any>, merch: Merch | undefined, myAttributes: Record<string, IMyAttribute>) {
    const [uaHeaderLogoData, uaHeaderNameData]: any = await this.getHeaderLogosData([
      appConstants.logoPaths.uaIconWhite,
      appConstants.logoPaths.uaNameLogoWhite,
    ])

    this.uaHeaderIconLogoData = uaHeaderLogoData
    this.uaHeaderNameLogoData = uaHeaderNameData
    return await this.buildTemplate(catalog, articlesData, options, myAttributes, merch)
  }

  async buildTemplate(catalog: CatalogDetails, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, merch: Merch | undefined) {
    const promises: Promise<void | number>[] = []
    this.imgSize = this.imageWidth * this.imageDefaultScaleFactor
    const slideSize = options.slideSize

    this.changePropsAsPerSlideSize(slideSize)

    const modelsMap = new Set<string>()
    const uniqueModelArticles: MyArticle[] = []
    for (const article of articles) {
      if (!modelsMap.has(article.ModelNumber)) {
        modelsMap.add(article.ModelNumber)
        uniqueModelArticles.push(article)
      }
    }

    let slideObjects: any[] = []
    let ttlSlides = 0
    for (const firstArticle of uniqueModelArticles) {
      slideObjects.push(await this.createHeaderLogo(
        this.leftPadding + (this.logoWidth * this.logoScaling) / 2,
        this.topPadding + (this.logoHeight * this.logoScaling) / 2,
      ))

      slideObjects.push(await this.createHeaderNameLogo(
        this.slideHeight - this.headerImageNameLogoProps.bottomPadding + (this.uaNameLogoWidth * this.uaNameLogoScaling) / 2,
        this.leftPadding,
      ))

      const modelNameHeight = this.getTextRequiredHeightBasedOnWidth(firstArticle.ModelName, this.slideWidth - this.modelNameProps.leftPadding, this.font, this.modelNameProps.fontSize, this.modelNameProps.fontWeight)
      const modelNameTop = this.modelNameProps.topPadding
      const modelNameLeft = this.modelNameProps.leftPadding
      const modelNameTextObject = this.createLabel(
        firstArticle.Id,
        ['ModelName'],
        '',
        modelNameTop,
        modelNameLeft,
        this.slideWidth - this.modelNameProps.leftPadding - this.circleObjProps.rightPadding - this.circleObjProps.width * 2,
        0,
        this.modelNameProps.fontSize,
        this.modelNameProps.fontWeight,
        'left',
        '#ffffff',
      )
      slideObjects.push(modelNameTextObject)

      const modelDetailsTop = modelNameTop + modelNameHeight
      const modelDetailsLeft = modelNameLeft
      const modelDetailsTextObject = this.createLabel(
        firstArticle.Id,
        ['ModelNumber', '_RetailPrice'],
        ' // ',
        modelDetailsTop,
        modelDetailsLeft,
        this.modelDetailsProps.width,
        0,
        this.modelDetailsProps.fontSize,
        this.modelDetailsProps.fontWeight,
        'left',
        '#ffffff',
      )
      slideObjects.push(modelDetailsTextObject)

      const imgLeft = this.slideWidth / 2
      const imgTop = modelDetailsTop + this.imgSize / 2
      slideObjects.push(
        this.createImage(
          firstArticle.Id,
          imgLeft,
          imgTop,
          this.imageDefaultScaleFactor,
          this.imageDefaultScaleFactor,
        ),
      )

      // left text boxes
      const weightTextBoxTop = modelDetailsTop + this.textBoxProps.topPadding
      const weightTextBox = this.createTextObject(
        'WEIGHT: ',
        weightTextBoxTop,
        this.textBoxProps.leftPadding,
        this.textBoxProps.width,
        0,
        this.textBoxProps.fontSize,
        this.textBoxProps.fontStyle,
        this.textBoxProps.fontWeight,
        this.textBoxProps.textAlign,
        this.textBoxProps.fill,
        false,
      )
      slideObjects.push(weightTextBox)

      const stackHeightTextBoxTop = weightTextBoxTop + this.textBoxProps.topPadding
      const stackHeightTextBox = this.createTextObject(
        'STACK HEIGHT: ',
        stackHeightTextBoxTop,
        this.textBoxProps.leftPadding,
        this.textBoxProps.width,
        0,
        this.textBoxProps.fontSize,
        this.textBoxProps.fontStyle,
        this.textBoxProps.fontWeight,
        this.textBoxProps.textAlign,
        this.textBoxProps.fill,
        false,
      )
      slideObjects.push(stackHeightTextBox)

      // bottom text box
      const needToKnowsTextBoxTop = this.slideHeight - this.needToKnowstextBoxProps.bottomPadding
      const needToKnowsTextBoxLeft = (this.slideWidth - this.needToKnowstextBoxProps.width) / 2
      const needToKnowsTextBox = this.createTextObject(
        'NEED TO KNOWS',
        needToKnowsTextBoxTop,
        needToKnowsTextBoxLeft,
        this.needToKnowstextBoxProps.width,
        0,
        this.needToKnowstextBoxProps.fontSize,
        this.needToKnowstextBoxProps.fontStyle,
        this.needToKnowstextBoxProps.fontWeight,
        this.needToKnowstextBoxProps.textAlign,
        this.needToKnowstextBoxProps.fill,
        false,
      )
      slideObjects.push(needToKnowsTextBox)

      const pointsTextBoxTop = needToKnowsTextBoxTop + this.ponitsTextBoxProps.topPadding
      const pointsTextBox = this.createTextObject(
        '1.\n2.\n3.\n4.\n5.',
        pointsTextBoxTop,
        this.ponitsTextBoxProps.leftPadding,
        this.slideWidth - this.ponitsTextBoxProps.leftPadding,
        0,
        this.textBoxProps.fontSize,
        this.textBoxProps.fontStyle,
        this.textBoxProps.fontWeight,
        this.textBoxProps.textAlign,
        this.textBoxProps.fill,
        false,
      )
      slideObjects.push(pointsTextBox)

      // right numbered round text boxes
      for (let i = 0; i < this.circleObjProps.bulletCount; i++) {
        const circleLeft = this.slideWidth - this.circleObjProps.rightPadding - this.circleObjProps.width
        const circleTop = modelNameTop + this.circleObjProps.topPadding + i * (this.circleObjProps.height + this.circleObjProps.circleSpacing)
        const circleObject = this.createCircleObject(
          circleLeft,
          circleTop,
          this.circleObjProps.width,
          this.circleObjProps.height,
          this.circleObjProps.fill,
        )
        slideObjects.push(circleObject)

        const textCenterTop = circleTop - this.circleObjProps.height / 2
        const additionalPadddingLeft = i === 0 ? 2 : i === 1 ? 1 : 0
        const textCenterLeft = circleLeft - this.circleObjProps.width / 2 + additionalPadddingLeft
        const numberTextBox = this.createTextObject(
          (i + 1).toString(),
          textCenterTop,
          textCenterLeft,
          this.circleObjProps.width,
          0,
          this.circleObjProps.fontSize,
          this.circleObjProps.fontStyle,
          this.circleObjProps.fontWeight,
          this.circleObjProps.textAlign,
          this.textBoxProps.fill,
          false,
        )
        slideObjects.push(numberTextBox)
      }

      ttlSlides++
      promises.push(this.createSlide(merch, `${firstArticle.ModelName} - ${padStart(ttlSlides.toString(), 3, '0')}`, slideObjects.slice(), options, this.imageDefaultScaleFactor)
        .catch((error) => {
          console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
        }))

      slideObjects = []
    }

    return Promise.all(promises)
      .then(() => {
        return ttlSlides
      })
  }

  changePropsAsPerSlideSize(slideSize: string) {
    this.slideHeight = merchConstants.slideSizeCanvasDimensions[slideSize].height
    this.slideWidth = merchConstants.slideSizeCanvasDimensions[slideSize].width
  }

  createSlide(merch, slideName, objects: any[], options: Record<string, any>, imageDefaultScaleFactor: number, articleIdList: number[] = []) {
    const folderNode = options.folder
    const sortOrder = Math.max(...folderNode.children.map(child => child.sortOrder), 0) + 1
    const folderStructure = findParentPath(folderNode)
    if (utils.isDefined(folderNode.children.find(node => node.label === slideName))) {
      let c = 0
      do {
        c++
        slideName = `${slideName} (${c})`
      } while (folderNode.children.find(node => node.label === slideName) !== undefined)
    }
    return merch!.addSlide(folderStructure.id, folderStructure.name, folderNode.sortOrder, slideName, objects, options.slideSize, imageDefaultScaleFactor, sortOrder, true, articleIdList, 'rgba(0, 0, 0, 1)')
  }

  getTextRequiredHeightBasedOnWidth(text: string, availableWidth: number, font: string, fontSize: number, fontWeight: string) {
    const minNumberOfLines = Math.ceil(utils.getTextWidth(text, `${fontWeight} ${fontSize}px ${font}`) / availableWidth)
    let multiLineSampleText = ''
    for (let index = 0; index < minNumberOfLines; index++) {
      multiLineSampleText += text
      if (index < minNumberOfLines - 1) {
        multiLineSampleText += '\n'
      }
    }
    return utils.getTextHeight(multiLineSampleText, fontSize, font, fabric)
  }

  async getHeaderLogosData(urls: string[]) {
    const loadImage = (url: string) => {
      return new Promise<string>((resolve) => {
        axios.get(url, { responseType: 'arraybuffer' }).then((res) => {
          const base64 = res.data ? `data:image/png;base64,${utils.arrayBufferToBase64(res.data)}` : null
          if (base64) {
            const settings = {
              width: this.logoWidth,
              height: this.logoHeight,
              scaleX: this.logoScaling,
              scaleY: this.logoScaling,
            }
            const image = new Image()
            image.onload = () => {
              resolve(utils.resizeImageAndGetBase64(image, settings) as string)
            }
            image.onerror = () => resolve('')
            image.src = base64
          }
          else {
            resolve('')
          }
        }).catch((err) => {
          console.log(err)
          resolve('')
        })
      })
    }

    const imagePromises = urls.map(loadImage)
    return Promise.all(imagePromises)
  }

  async createHeaderLogo(top: number, left: number) {
    return {
      type: merchConstants.objectTypes.image,
      top,
      left,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 90,
      scaleY: this.logoScaling,
      scaleX: this.logoScaling,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data: this.uaHeaderIconLogoData,
    }
  }

  async createHeaderNameLogo(top: number, left: number) {
    return {
      type: 'image',
      top,
      left,
      width: this.uaNameLogoWidth,
      height: this.uaNameLogoHeight,
      angle: 90,
      scaleY: this.uaNameLogoScaling,
      scaleX: this.uaNameLogoScaling,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data: this.uaHeaderNameLogoData,
    }
  }

  createLabel(articleId, attributes, articlePropsSeparator, top, left, width, height, fontSize, fontWeight, textAlign, fill) {
    if (!Array.isArray(attributes)) {
      attributes = [attributes]
    }
    if (!attributes || attributes.length === 0) { return }

    return {
      type: merchConstants.objectTypes.articleDetails,
      top,
      left,
      width,
      height,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      fill,
      flipX: false,
      flipY: false,
      customOptions: {
        articleProps: attributes.slice(),
        articleBLCoords: { x: 0, y: 0 },
        separator: articlePropsSeparator,
      },
      fontFamily: this.font,
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign,
      articleId,
    }
  }

  createTextObject(text: string, top: number, left: number, width: number, height: number, fontSize: number, fontStyle: string, fontWeight: string, textAlign: string, fill: string, templateObject: boolean) {
    return {
      type: merchConstants.objectTypes.textBox,
      top,
      left,
      width,
      height,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      text,
      fill,
      backgroundColor: '',
      fontFamily: this.font,
      fontSize,
      fontStyle,
      fontWeight,
      textAlign,
      locked: false,
      templateObject,
      isOriginTopLeft: true,
    }
  }

  createImage(articleId, left, top, scaleX, scaleY) {
    return {
      type: merchConstants.objectTypes.articleImage,
      top,
      left,
      width: this.imageWidth,
      height: this.imageHeight,
      articleId,
      angle: 0,
      scaleX,
      scaleY,
      flipX: false,
      flipY: false,
      locked: false,
      shouldAllowTemplateBigImagesToResize: true,
    }
  }

  createCircleObject(left, top, width, height, fill) {
    return {
      type: merchConstants.objectTypes.circle,
      top,
      left,
      width,
      height,
      fill,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      locked: false,
      radius: width,
      angle: 0,
      templateObject: false,
    }
  }
}

export default new FootwearTechSheet()
